import React, { useContext, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Breadcrumbs } from '@/components/ui/breadcrumb'
import { PermissionsEnum } from 'api/Identity/Types/PermissionsEnum'
import { Loading } from 'components/Loading'
import { Spinner } from 'components/Spinner'
import PageNotFound from 'routes/PageNotFound/PageNotFound'
import { useApiClient } from 'utils/api/ApiClient'
import { ClientsApiClient } from 'utils/api/clientsApi'
import { PartnershipsApiClient } from 'utils/api/partnershipsApi'
import { useCallbackWithForceRefresh } from 'utils/api/useCallbackWithForceRefresh'
import { useFetchOne } from 'utils/api/useFetch'
import { AuthSessionContext } from 'utils/AuthContext'
import { introducingBrokerDetailsRouteUrl, links } from 'utils/featureToggles'
import { hasPermission } from 'utils/permissionsValidator'

import IntroducingBrokerClientDetails from './IntroducingBrokerClientsDetails'
import IntroducingBrokerDeals from './IntroducingBrokerDeals'
import IntroducingBrokerDetailsPage from './IntroducingBrokerDetails'
import IntroducingBrokerMTNetworkPage from './IntroducingBrokerMTNetworkPage'
import IntroducingBrokerReferralRestrictionsPage from './IntroducingBrokerReferralRestrictionsPage'
import IntroducingBrokerTradingAccounts from './IntroducingBrokerTradingAccounts'
import IntroducingBrokerRebatesPage from './Rebates/IntroducingBrokerRebatesPage'

export enum IntroducingBrokerTab {
  Details = 'details',
  Client = 'clients',
  Trading_accounts = 'trading-accounts',
  Deals = 'deals',
  ReferralsRestrictions = 'referral-restrictions',
  Rebates = 'rebates',
  Network = 'network',
}

const ResolveIBRoute = (): JSX.Element => {
  const [crumbs, setCrumbs] = useState([
    { title: 'Introducing Brokers', link: links.recordsIntroducingBrokers.url },
  ])
  const partnershipsApiClient = useApiClient(PartnershipsApiClient)
  const clientsApiClient = useApiClient(ClientsApiClient)

  const [auth] = useContext(AuthSessionContext)
  const { id, slug } = useParams<{ id: string; slug: IntroducingBrokerTab }>()

  const permissions = auth?.permissions || []

  const { callback: ibCallback, forceRefresh: ibForceRefresh } = useCallbackWithForceRefresh(
    () => (id ? partnershipsApiClient.getIntroducingBrokerById(id) : Promise.resolve(null)),
    [id]
  )
  const { data: introducingBroker, isLoading: introducingBrokerLoading } = useFetchOne(ibCallback)

  const { callback: clientCallback, forceRefresh: clientForceRefresh } =
    useCallbackWithForceRefresh(
      () =>
        introducingBroker?.client.id
          ? clientsApiClient.getClient(introducingBroker?.client.id)
          : Promise.resolve(null),
      [introducingBroker?.client.id]
    )

  const { data: clientDetailed, isLoading: clientDetailedLoading } = useFetchOne(clientCallback)

  const { callback: loyaltyCallback, forceRefresh: loyaltyForceRefresh } =
    useCallbackWithForceRefresh(
      () => (id ? partnershipsApiClient.getIntroducingBrokerLoyalty(id) : Promise.resolve(null)),
      [id]
    )

  const { data: loyalty, isLoading: loyaltyLoading } = useFetchOne(loyaltyCallback)

  const matchesSlug = (introducingBrokerTab?: IntroducingBrokerTab) => {
    if (!introducingBrokerTab) {
      return false
    }
    return (
      introducingBrokerTab === IntroducingBrokerTab.Details ||
      introducingBrokerTab === IntroducingBrokerTab.Client ||
      introducingBrokerTab === IntroducingBrokerTab.Trading_accounts ||
      introducingBrokerTab === IntroducingBrokerTab.Deals ||
      introducingBrokerTab === IntroducingBrokerTab.ReferralsRestrictions ||
      introducingBrokerTab === IntroducingBrokerTab.Rebates ||
      introducingBrokerTab === IntroducingBrokerTab.Network
    )
  }

  useEffect(() => {
    if (introducingBroker) {
      const [crumb1, crumb2] = [
        ...crumbs,
        {
          title: introducingBroker.name || '',
          link: introducingBrokerDetailsRouteUrl(
            introducingBroker,
            slug
              ? matchesSlug(slug)
                ? slug
                : IntroducingBrokerTab.Details
              : IntroducingBrokerTab.Details
          ),
        },
      ]
      setCrumbs([crumb1, crumb2])
    }
  }, [introducingBroker]) // eslint-disable-line react-hooks/exhaustive-deps

  const innerRender = () => {
    if (!matchesSlug(slug)) {
      return <PageNotFound />
    }
    if (introducingBrokerLoading || clientDetailedLoading || loyaltyLoading || !clientDetailed) {
      return <Spinner />
    }
    if (!introducingBroker) {
      return <PageNotFound />
    }
    if (
      slug === IntroducingBrokerTab.Details &&
      hasPermission(permissions, PermissionsEnum.CanViewIntroducingBroker)
    ) {
      return (
        <React.Fragment>
          <Breadcrumbs crumbs={crumbs} />
          <IntroducingBrokerDetailsPage
            clientDetailed={clientDetailed}
            data={{
              ...introducingBroker,
              activeClients: loyalty?.activeClients,
              volumeRatio: loyalty?.volumeRatio,
            }}
            forceRefresh={() => {
              ibForceRefresh()
              clientForceRefresh()
              loyaltyForceRefresh()
            }}
          />
        </React.Fragment>
      )
    }
    if (
      slug === IntroducingBrokerTab.Client &&
      hasPermission(permissions, PermissionsEnum.CanViewIntroducingBroker)
    ) {
      return (
        <React.Fragment>
          <Breadcrumbs crumbs={crumbs} />
          <IntroducingBrokerClientDetails data={introducingBroker} />
        </React.Fragment>
      )
    }
    if (
      slug === IntroducingBrokerTab.Trading_accounts &&
      hasPermission(permissions, PermissionsEnum.CanViewIntroducingBroker)
    ) {
      return (
        <React.Fragment>
          <Breadcrumbs crumbs={crumbs} />
          <IntroducingBrokerTradingAccounts data={introducingBroker} />
        </React.Fragment>
      )
    }
    if (
      slug === IntroducingBrokerTab.Deals &&
      hasPermission(permissions, PermissionsEnum.CanViewIntroducingBroker)
    ) {
      return (
        <React.Fragment>
          <Breadcrumbs crumbs={crumbs} />
          <IntroducingBrokerDeals data={introducingBroker} />
        </React.Fragment>
      )
    }
    if (
      slug === IntroducingBrokerTab.ReferralsRestrictions &&
      hasPermission(permissions, PermissionsEnum.CanBrowseIntroducingBrokers)
    ) {
      return (
        <React.Fragment>
          <Breadcrumbs crumbs={crumbs} />
          <IntroducingBrokerReferralRestrictionsPage data={introducingBroker} />
        </React.Fragment>
      )
    }
    if (
      slug === IntroducingBrokerTab.Rebates &&
      hasPermission(permissions, PermissionsEnum.CanViewIntroducingBrokerRebate)
    ) {
      return (
        <React.Fragment>
          <Breadcrumbs crumbs={crumbs} />
          <IntroducingBrokerRebatesPage data={introducingBroker} />
        </React.Fragment>
      )
    }
    if (
      !!introducingBroker.multiTierRegisteredAt &&
      slug === IntroducingBrokerTab.Network &&
      hasPermission(permissions, PermissionsEnum.CanViewMultiTierNetwork)
    ) {
      return (
        <React.Fragment>
          <Breadcrumbs crumbs={crumbs} />
          <IntroducingBrokerMTNetworkPage data={introducingBroker} />
        </React.Fragment>
      )
    }
    return <PageNotFound />
  }

  return (
    <Loading
      isLoading={
        (introducingBrokerLoading || clientDetailedLoading || loyaltyLoading) && !matchesSlug(slug)
      }
    >
      {innerRender()}
    </Loading>
  )
}

export default ResolveIBRoute
